<template>
  <!--
    TASK-SF-09 -- DESIGN-11, the student profile EDIT view. Its own subtree
    inside ProfileView; the volunteer edit path (`activeEdit` /
    `createUpdateProfileRequestBody()`) is untouched. The student path NEVER
    calls PUT /api/user -- only PATCH /api/student/profile, and only with the
    keys the user actually changed. `citySlug` and `cityManual` are mutually
    exclusive by construction (the manual toggle / auto-fallback), mirroring
    the server / database invariant so a rejectable request can never be
    built.

    Visual parity pass (student-foundation-redesign): geometry/spacing/color
    are restyled toward the prototype using component-scoped literals only --
    no shared token, font, or _form.scss edits.
  -->
  <form
    class="student-profile-edit"
    data-testid="student-profile-edit"
    @submit.prevent="onSave"
  >
    <button
      type="button"
      class="student-profile-edit__back uc-link-button"
      data-testid="spe-back"
      @click="$emit('done')"
    >
      <student-home-icon name="arrow" :size="18" />
      {{ $t('profile.student.editForm.back') }}
    </button>

    <!-- SX-01 (DESIGN-19): V2's `.flow-heading` -- 50px icon tile, eyebrow,
         h1, lead -- then the form inside one `.flow-card`. -->
    <div class="student-profile-edit__heading">
      <span class="student-profile-edit__heading-icon" aria-hidden="true">
        <student-icon name="user" :size="22" />
      </span>
      <div class="student-profile-edit__heading-text">
        <p class="student-profile-edit__eyebrow">
          {{ $t('profile.student.editForm.eyebrow') }}
        </p>
        <h1 class="student-profile-edit__title">
          {{ $t('profile.student.editForm.heading') }}
        </h1>
        <p class="student-profile-edit__lead">
          {{ $t('profile.student.editForm.lead') }}
        </p>
      </div>
    </div>

    <div
      v-if="loadError"
      class="alert alert-danger"
      role="alert"
      data-testid="spe-load-error"
    >
      {{ $t('profile.student.editForm.loadError') }}
    </div>

    <div v-else-if="!loading" class="student-profile-edit__card">
      <div class="student-profile-edit__row">
        <div class="uc-form-element">
          <label for="spe-first" class="uc-form-label">{{
            $t('profile.student.editForm.firstName')
          }}</label>
          <input
            id="spe-first"
            class="uc-form-text-input"
            type="text"
            data-testid="spe-first-name"
            :aria-invalid="firstNameError ? 'true' : null"
            :aria-describedby="firstNameError ? 'spe-first-error' : null"
            v-model.trim="form.firstName"
          />
          <p
            v-if="firstNameError"
            id="spe-first-error"
            class="uc-form-error"
            role="alert"
            data-testid="spe-first-name-error"
          >
            {{ firstNameError }}
          </p>
        </div>
        <div class="uc-form-element">
          <label for="spe-last" class="uc-form-label">{{
            optionalLabel($t('profile.student.editForm.lastName'))
          }}</label>
          <input
            id="spe-last"
            class="uc-form-text-input"
            type="text"
            data-testid="spe-last-name"
            v-model.trim="form.lastName"
          />
        </div>
      </div>

      <!-- P42-08 / P42-09: the مقطع and the پایه are mandatory, and so is the
           رشته wherever the مقطع offers one. The error sits under the field,
           is announced, and the first invalid field takes focus on save. -->
      <div class="student-profile-edit__row">
        <div class="student-profile-edit__cell">
          <dropdown-list
            id="spe-stage"
            class="student-profile-edit__field"
            data-testid="spe-stage"
            :label="$t('profile.student.editForm.stage')"
            :disabled-option="$t('profile.student.editForm.stagePlaceholder')"
            :options="stageOptions"
            :option-display="stageDisplay"
            :value="form.educationStage"
            :invalid="Boolean(academicErrors.stage)"
            :describedby="academicErrors.stage ? 'spe-stage-error' : null"
            @input="onStageInput"
          />
          <p
            v-if="academicErrors.stage"
            id="spe-stage-error"
            class="uc-form-error"
            role="alert"
            data-testid="spe-stage-error"
          >
            {{ academicErrors.stage }}
          </p>
        </div>
        <div class="student-profile-edit__cell">
          <dropdown-list
            id="spe-grade"
            class="student-profile-edit__field"
            data-testid="spe-grade"
            :label="$t('profile.student.editForm.grade')"
            :disabled="!form.educationStage"
            :disabled-option="
              form.educationStage
                ? $t('profile.student.editForm.gradePlaceholder')
                : $t('profile.student.editForm.gradeDisabledPlaceholder')
            "
            :options="gradeOptions"
            :option-display="gradeDisplay"
            :value="form.educationGrade"
            :invalid="Boolean(academicErrors.grade)"
            :describedby="academicErrors.grade ? 'spe-grade-error' : null"
            @input="onGradeInput"
          />
          <p
            v-if="academicErrors.grade"
            id="spe-grade-error"
            class="uc-form-error"
            role="alert"
            data-testid="spe-grade-error"
          >
            {{ academicErrors.grade }}
          </p>
        </div>
      </div>

      <!--
        Data-driven only: rendered when the SELECTED STAGE's reference payload
        carries study fields (`selectedStage.studyFields.length`). No
        hardcoded grade numbers/names -- grade never gates this control, and
        changing the grade never clears an already-chosen study field, since
        applicability is stage-backed in the reference contract, not
        grade-backed.
      -->
      <div v-if="studyFieldOptions.length" class="student-profile-edit__cell">
        <dropdown-list
          id="spe-field"
          class="student-profile-edit__field"
          data-testid="spe-study-field"
          :label="$t('profile.student.editForm.studyField')"
          :disabled-option="
            $t('profile.student.editForm.studyFieldPlaceholder')
          "
          :options="studyFieldOptions"
          :option-display="studyFieldDisplay"
          :value="form.studyField"
          :invalid="Boolean(academicErrors.studyField)"
          :describedby="academicErrors.studyField ? 'spe-field-error' : null"
          @input="onStudyFieldInput"
        />
        <p
          v-if="academicErrors.studyField"
          id="spe-field-error"
          class="uc-form-error"
          role="alert"
          data-testid="spe-study-field-error"
        >
          {{ academicErrors.studyField }}
        </p>
      </div>

      <fieldset class="student-profile-edit__fieldset">
        <legend>{{ $t('profile.student.editForm.residenceGroup') }}</legend>
        <!-- V2 keeps استان and شهر side by side (one column on the phone tier) -->
        <div class="student-profile-edit__row">
          <dropdown-list
            id="spe-province"
            class="student-profile-edit__field"
            data-testid="spe-province"
            :label="optionalLabel($t('profile.student.editForm.province'))"
            :disabled-option="
              $t('profile.student.editForm.provincePlaceholder')
            "
            :options="provinceOptions"
            :option-display="provinceDisplay"
            :value="form.province"
            @input="onProvinceInput"
          />

          <!-- SX-01 (DESIGN-19): V2 offers manual entry as the LAST option of
             the city select («در فهرست نیست؛ خودم می‌نویسم»), which reveals
             the «نام شهر یا روستا» input. Lookup and manual stay mutually
             exclusive exactly as before (TASK-SF-09 / D-open-5). -->
          <template v-if="!manualCity">
            <dropdown-list
              id="spe-city"
              class="student-profile-edit__field"
              data-testid="spe-city"
              :label="optionalLabel($t('profile.student.editForm.city'))"
              :disabled="!form.province"
              :disabled-option="
                form.province
                  ? $t('profile.student.editForm.cityPlaceholder')
                  : $t('profile.student.editForm.cityDisabledPlaceholder')
              "
              :options="cityOptionsWithManual"
              :option-display="cityDisplayWithManual"
              :value="form.citySlug"
              @input="onCitySlugInput"
            />
          </template>
          <div v-else class="uc-form-element">
            <label for="spe-city-manual" class="uc-form-label">{{
              optionalLabel($t('profile.student.editForm.manualCityLabel'))
            }}</label>
            <input
              id="spe-city-manual"
              class="uc-form-text-input"
              type="text"
              data-testid="spe-city-manual"
              :maxlength="freeTextMax"
              :placeholder="$t('profile.student.editForm.manualPlaceholder')"
              :value="form.cityManual"
              @input="onCityManualInput($event.target.value)"
            />
            <button
              type="button"
              class="student-profile-edit__link"
              data-testid="spe-manual-toggle"
              @click="toggleManualCity"
            >
              {{ $t('profile.student.editForm.manualToggleBack') }}
            </button>
          </div>
        </div>
      </fieldset>

      <div class="uc-form-element">
        <label for="spe-school" class="uc-form-label">{{
          optionalLabel($t('profile.student.editForm.schoolName'))
        }}</label>
        <input
          id="spe-school"
          class="uc-form-text-input"
          type="text"
          data-testid="spe-school-name"
          :maxlength="freeTextMax"
          :placeholder="$t('profile.student.editForm.schoolPlaceholder')"
          v-model.trim="form.schoolName"
        />
      </div>

      <div class="uc-form-element">
        <label for="spe-email" class="uc-form-label">{{
          optionalLabel($t('profile.student.editForm.email'))
        }}</label>
        <input
          id="spe-email"
          class="uc-form-text-input ltr"
          type="email"
          inputmode="email"
          dir="ltr"
          data-testid="spe-email"
          placeholder="name@example.com"
          :aria-invalid="emailError ? 'true' : null"
          :aria-describedby="emailError ? 'spe-email-error' : null"
          v-model.trim="form.email"
        />
        <p
          v-if="emailError"
          id="spe-email-error"
          class="uc-form-error"
          role="alert"
          data-testid="spe-email-error"
        >
          {{ emailError }}
        </p>
      </div>

      <div class="uc-form-element">
        <label for="spe-phone" class="uc-form-label">{{
          $t('profile.student.editForm.phone')
        }}</label>
        <input
          id="spe-phone"
          class="uc-form-text-input ltr"
          type="text"
          dir="ltr"
          readonly
          data-testid="spe-phone"
          :value="phone"
          :placeholder="$t('profile.student.editForm.phonePlaceholder')"
          aria-describedby="spe-phone-note"
        />
        <p class="notice notice--soft" data-testid="spe-phone-note">
          <student-icon name="shield" :size="16" />
          <span>{{ $t('profile.student.editForm.phoneNote') }}</span>
        </p>
        <!-- P43-04: an account created with an email or a Google login has no
             number yet; it can verify one here. A number that is already set
             stays read-only. -->
        <student-phone-attach v-if="!phone" @attached="onPhoneAttached" />
        <p
          v-if="phoneAddedNotice"
          class="success"
          role="status"
          data-testid="spe-phone-added"
        >
          {{ $t('profile.student.editForm.phoneAttach.added') }}
        </p>
      </div>

      <p
        v-if="saveError"
        class="alert alert-danger"
        role="alert"
        data-testid="spe-save-error"
      >
        {{ saveError }}
      </p>
      <p
        v-if="savedNotice"
        class="success"
        role="status"
        data-testid="spe-saved"
      >
        {{ $t('profile.student.editForm.saved') }}
      </p>

      <button
        class="uc-form-button student-profile-edit__save"
        type="submit"
        data-testid="spe-save"
        :disabled="saving || !canSave"
      >
        <span v-if="saving" class="uc-form-spinner" aria-hidden="true"></span>
        {{ $t('profile.student.editForm.save') }}
      </button>
    </div>
  </form>
</template>

<script>
import Validator from 'validator'
import DropdownList from '@/components/DropdownList'
import StudentService from '@/services/StudentService'
import StudentPhoneAttach from './StudentPhoneAttach.vue'
import { STUDENT_FREE_TEXT_MAX } from '@/utils/student-profile-limits'
import StudentIcon from './StudentIcon.vue'
import StudentHomeIcon from '@/components/StudentHome/StudentHomeIcon.vue'

// SX-01: the city select's manual-entry sentinel (never a real slug, never
// sent).
const MANUAL_CITY_OPTION = '__manual'

function blankForm() {
  return {
    firstName: '',
    lastName: '',
    educationStage: '',
    educationGrade: '',
    studyField: '',
    province: '',
    citySlug: '',
    cityManual: '',
    schoolName: '',
    email: '',
  }
}

export default {
  name: 'StudentProfileEdit',
  components: {
    DropdownList,
    StudentIcon,
    StudentHomeIcon,
    StudentPhoneAttach,
  },
  data() {
    return {
      freeTextMax: STUDENT_FREE_TEXT_MAX,
      loading: true,
      loadError: false,
      saving: false,
      saveError: null,
      savedNotice: false,
      phoneAddedNotice: false,
      emailError: null,
      firstNameError: null,
      // P42-08: inline errors for the three mandatory academic fields.
      academicErrors: { stage: null, grade: null, studyField: null },
      manualCity: false,
      stages: [],
      provinces: [],
      cities: [],
      phone: '',
      // The display name of a previously-saved LOOKUP city, kept only so an
      // auto-fallback to manual entry (D-open-5) can show the real name the
      // server already returned instead of inventing one.
      seededCityDisplayName: '',
      form: blankForm(),
      original: blankForm(),
    }
  },
  computed: {
    selectedStage() {
      return this.stages.find(s => s.name === this.form.educationStage) || null
    },
    stageOptions() {
      return this.stages.map(s => s.name)
    },
    stageDisplay() {
      return this.toMap(this.stages)
    },
    gradeOptions() {
      return this.selectedStage
        ? (this.selectedStage.grades || []).map(g => g.name)
        : []
    },
    gradeDisplay() {
      return this.toMap(
        this.selectedStage ? this.selectedStage.grades || [] : []
      )
    },
    // Data-driven: the reference payload nests studyFields under the STAGE,
    // never the grade. `v-if="studyFieldOptions.length"` on the control is
    // the only gate -- no grade heuristic.
    studyFieldOptions() {
      return this.selectedStage
        ? (this.selectedStage.studyFields || []).map(f => f.name)
        : []
    },
    studyFieldDisplay() {
      return this.toMap(
        this.selectedStage ? this.selectedStage.studyFields || [] : []
      )
    },
    provinceOptions() {
      return this.provinces.map(p => p.name)
    },
    provinceDisplay() {
      return this.toMap(this.provinces)
    },
    cityOptions() {
      return this.cities.map(c => c.name)
    },
    cityDisplay() {
      return this.toMap(this.cities)
    },
    // SX-01: V2's manual-entry affordance is the last option of the select.
    // The sentinel never reaches the form / the PATCH -- choosing it only
    // switches the control (see onCitySlugInput).
    cityOptionsWithManual() {
      return [...this.cityOptions, MANUAL_CITY_OPTION]
    },
    cityDisplayWithManual() {
      return {
        ...this.cityDisplay,
        [MANUAL_CITY_OPTION]: this.$t('profile.student.editForm.manualOption'),
      }
    },
    // Only-changed-keys detection.
    changedKeys() {
      const keys = []
      const cmp = [
        'firstName',
        'lastName',
        'educationStage',
        'educationGrade',
        'studyField',
        'province',
        'schoolName',
        'email',
      ]
      for (const k of cmp) {
        if ((this.form[k] || '') !== (this.original[k] || '')) keys.push(k)
      }
      // city: exactly one of citySlug / cityManual per the toggle.
      if (this.manualCity) {
        if ((this.form.cityManual || '') !== (this.original.cityManual || ''))
          keys.push('cityManual')
      } else if (
        (this.form.citySlug || '') !== (this.original.citySlug || '')
      ) {
        keys.push('citySlug')
      }
      return keys
    },
    isDirty() {
      return this.changedKeys.length > 0
    },
    // P42-08: the مقطع and پایه are mandatory, and so is the رشته when the
    // مقطع offers one. `undecided` is a real رشته, so it counts here (whether
    // it is enough for the AI Tutor is Setup's rule).
    academicsComplete() {
      return (
        Boolean(this.form.educationStage) &&
        Boolean(this.form.educationGrade) &&
        (!this.studyFieldOptions.length || Boolean(this.form.studyField))
      )
    },
    // A legacy profile that is already incomplete can be saved (to surface
    // what is missing and complete it) even before anything else changed.
    canSave() {
      return this.isDirty || (!this.loading && !this.academicsComplete)
    },
  },
  created() {
    this.load()
  },
  methods: {
    toMap(list) {
      return list.reduce((acc, i) => {
        acc[i.name] = i.displayName || i.name
        return acc
      }, {})
    },
    async load() {
      this.loading = true
      this.loadError = false
      try {
        const [ref, profile] = await Promise.all([
          StudentService.getEducationReference(),
          StudentService.getProfile(),
        ])
        this.stages = Array.isArray(ref.stages) ? ref.stages : []
        this.provinces = Array.isArray(ref.provinces) ? ref.provinces : []
        this.seedFrom(profile)
        if (this.form.province) {
          await this.fetchCities(this.form.province)
          this.applyCityAutoFallback()
        }
      } catch (e) {
        this.loadError = true
      } finally {
        this.loading = false
      }
    },
    // P43-04: the number was verified and attached to this account. Show it
    // read-only like any other number, and refresh the signed-in user once so
    // the rest of the app sees the same account state.
    onPhoneAttached(profile) {
      this.phone = (profile && profile.phone) || this.phone
      this.phoneAddedNotice = true
      if (this.$store && this.$store.dispatch) {
        Promise.resolve(this.$store.dispatch('user/fetchUser')).catch(() => {})
      }
    },
    seedFrom(p) {
      this.phone = p.phone || ''
      const f = blankForm()
      f.firstName = p.firstName || ''
      f.lastName = p.lastName || ''
      f.educationStage = (p.educationStage && p.educationStage.name) || ''
      f.educationGrade = (p.educationGrade && p.educationGrade.name) || ''
      f.studyField = (p.studyField && p.studyField.name) || ''
      f.province = (p.province && p.province.name) || ''
      f.schoolName = p.schoolName || ''
      f.email = p.email || ''
      this.seededCityDisplayName = ''
      if (p.city && p.city.type === 'lookup') {
        f.citySlug = p.city.name
        this.manualCity = false
        this.seededCityDisplayName = p.city.displayName || p.city.name
      } else if (p.city && p.city.type === 'manual') {
        f.cityManual = p.city.value
        this.manualCity = true
      }
      this.form = f
      this.original = { ...f }
    },
    async fetchCities(provinceSlug) {
      try {
        const res = await StudentService.getCitiesByProvince(provinceSlug)
        this.cities = Array.isArray(res.cities) ? res.cities : []
      } catch (e) {
        this.cities = []
      }
    },
    // D-open-5 (approved): auto-switch to manual city entry when the
    // selected province has no lookup cities, or a previously-saved lookup
    // city isn't in the fetched list. Runs only right after the initial
    // load, before the user has touched anything -- it re-baselines
    // `original` to the same values so the form does NOT appear dirty (and
    // nothing is PATCHed) purely because of this display-mode switch. Never
    // invents a locality: the manual field is seeded from the real
    // `displayName` the server already returned, or left blank.
    applyCityAutoFallback() {
      if (this.manualCity) return
      const noCities = this.cities.length === 0
      const savedMissing =
        this.form.citySlug &&
        !this.cities.some(c => c.name === this.form.citySlug)
      if (!noCities && !savedMissing) return
      this.manualCity = true
      this.form.citySlug = ''
      this.form.cityManual = this.seededCityDisplayName || ''
      this.original = { ...this.form }
    },
    onStageInput(stage) {
      // Changing مقطع clears the dependent پایه / رشته. Changing پایه alone
      // never clears رشته -- study-field validity is stage-backed, not
      // grade-backed, in the reference contract.
      this.form.educationStage = stage
      this.form.educationGrade = ''
      this.form.studyField = ''
      this.academicErrors = { stage: null, grade: null, studyField: null }
    },
    onGradeInput(grade) {
      this.form.educationGrade = grade
      this.academicErrors.grade = null
    },
    onStudyFieldInput(field) {
      this.form.studyField = field
      this.academicErrors.studyField = null
    },
    // P42-08: nothing incomplete is ever sent. Returns the id of the first
    // invalid field (DOM order), or null when the academics are complete.
    validateAcademics() {
      const errors = { stage: null, grade: null, studyField: null }
      let firstId = null
      if (!this.form.educationStage) {
        errors.stage = this.$t('profile.student.editForm.stageRequired')
        firstId = firstId || 'spe-stage'
      }
      if (!this.form.educationGrade) {
        errors.grade = this.$t('profile.student.editForm.gradeRequired')
        firstId = firstId || (this.form.educationStage ? 'spe-grade' : null)
      }
      if (
        this.form.educationStage &&
        this.studyFieldOptions.length &&
        !this.form.studyField
      ) {
        errors.studyField = this.$t(
          'profile.student.editForm.studyFieldRequired'
        )
        firstId = firstId || 'spe-field'
      }
      this.academicErrors = errors
      return firstId
    },
    async onProvinceInput(province) {
      // Changing استان clears any chosen شهر (both forms) and reloads the
      // list. If the new province has no cities at all, auto-fall-back to
      // manual entry (D-open-5.A) -- the toggle can still switch back.
      this.form.province = province
      this.form.citySlug = ''
      this.form.cityManual = ''
      this.cities = []
      if (province) {
        await this.fetchCities(province)
        if (!this.manualCity && this.cities.length === 0) {
          this.manualCity = true
        }
      }
    },
    onCitySlugInput(slug) {
      if (slug === MANUAL_CITY_OPTION) {
        // V2's «در فهرست نیست؛ خودم می‌نویسم» option: swap to free text.
        this.manualCity = true
        this.form.citySlug = ''
        return
      }
      this.form.citySlug = slug
      this.form.cityManual = '' // lookup and manual are mutually exclusive
    },
    // P40 TR-043 (owner decision): every truly optional field -- last name,
    // school, e-mail, province, city (select or manual) -- carries the same "(اختیاری)" text
    // marker. DropdownList (a frozen shared component) takes a plain string,
    // so the text form is the one all labels can share.
    optionalLabel(label) {
      return `${label} (${this.$t('profile.student.editForm.optional')})`
    },
    onCityManualInput(val) {
      this.form.cityManual = val
      this.form.citySlug = ''
    },
    toggleManualCity() {
      this.manualCity = !this.manualCity
      // Switching modes clears the other control's value.
      if (this.manualCity) this.form.citySlug = ''
      else this.form.cityManual = ''
    },
    focusField(id) {
      this.$nextTick(() => {
        const el = document.getElementById(id)
        if (el) el.focus()
      })
    },
    async onSave() {
      if (this.saving || !this.canSave) return
      this.saveError = null
      this.savedNotice = false
      this.emailError = null
      this.firstNameError = null

      // نام is required; نام خانوادگی and every other field stay optional.
      // The academics are mandatory too (P42-08): both are checked so the
      // student sees every missing field at once, and the first one in page
      // order takes focus. Nothing is sent while either is incomplete.
      const academicsFocus = this.validateAcademics()
      const nameMissing = !this.form.firstName || !this.form.firstName.trim()
      if (nameMissing) {
        this.firstNameError = this.$t(
          'profile.student.editForm.firstNameRequired'
        )
        this.focusField('spe-first')
        return
      }
      if (academicsFocus) {
        this.focusField(academicsFocus)
        return
      }

      // A saved e-mail can be changed but not removed (C-1): stop here, so
      // `email: null` is never built and the server's rejection is never
      // mislabelled as an e-mail conflict.
      if (
        this.original.email &&
        this.changedKeys.includes('email') &&
        !(this.form.email || '').trim()
      ) {
        this.emailError = this.$t(
          'profile.student.editForm.emailRemoveNotAllowed'
        )
        this.focusField('spe-email')
        return
      }

      const patch = {}
      for (const k of this.changedKeys) {
        // Subway accepts a last name only as a string (`''` clears it), never
        // `null` (TR-002).
        if (k === 'lastName') patch.lastName = this.form.lastName || ''
        else if (k === 'citySlug') patch.citySlug = this.form.citySlug || null
        else if (k === 'cityManual')
          patch.cityManual = this.form.cityManual || null
        else patch[k] = this.form[k] === '' ? null : this.form[k]
      }
      // Client-side email format guard (only when a non-empty email changed).
      if (
        typeof patch.email === 'string' &&
        patch.email &&
        !Validator.isEmail(patch.email)
      ) {
        this.emailError = this.$t('profile.student.editForm.emailFormatError')
        return
      }
      // Never both city keys in one request.
      if ('citySlug' in patch && 'cityManual' in patch) {
        delete patch.cityManual
      }

      this.saving = true
      try {
        const updated = await StudentService.updateProfile(patch)
        this.seedFrom(updated) // rehydrate from the response
        this.savedNotice = true
        this.$emit('saved', updated)
      } catch (err) {
        // P40 NEW-409: errorFromHttpResponse passes a plain Axios rejection
        // through unchanged, and axios 1.5 puts neither `.status` nor the
        // body on the error itself. Read both shapes (the pattern LoginView
        // uses for registration); the shared helper stays as it is.
        const status =
          (err && err.status) ||
          (err && err.response && err.response.status) ||
          null
        const bodyMessage =
          (err &&
            err.response &&
            err.response.data &&
            (err.response.data.message || err.response.data.err)) ||
          ''
        const message = bodyMessage || (err && err.message) || ''
        const bodyCode =
          (err &&
            err.response &&
            err.response.data &&
            err.response.data.code) ||
          (err && err.code) ||
          ''
        if (
          status === 422 &&
          bodyCode === 'STUDENT_PROFILE_STUDY_FIELD_REQUIRED'
        ) {
          // P42-08: the server refused an incomplete combination; show it on
          // the field, exactly like the client-side check.
          this.academicErrors.studyField = this.$t(
            'profile.student.editForm.studyFieldRequired'
          )
          this.focusField('spe-field')
        } else if (
          status === 422 &&
          bodyCode === 'STUDENT_PROFILE_GRADE_REQUIRED'
        ) {
          this.academicErrors.grade = this.$t(
            'profile.student.editForm.gradeRequired'
          )
          this.focusField(this.form.educationStage ? 'spe-grade' : 'spe-stage')
        } else if (status === 409 && /email/i.test(message)) {
          this.emailError = this.$t('profile.student.editForm.emailConflict')
        } else if (status === 422 && /already in use/i.test(message)) {
          this.emailError = this.$t('profile.student.editForm.emailConflict')
        } else if (status === 422 && /email/i.test(message)) {
          // e.g. Subway's "Email is not a valid email format"
          this.emailError = this.$t('profile.student.editForm.emailFormatError')
        } else {
          this.saveError = this.$t('profile.student.editForm.saveError')
        }
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// Component-scoped visual tokens (student-foundation-redesign visual parity
// pass). LOCAL to this file -- no shared _colors.scss / _form.scss / font
// edits.
// V2 `--error` (validation) -- the one value the token layer does not carry.
$student-error: #d14545;

.student-profile-edit {
  // Student Journey V2: the shared Student page box (see StudentProfileCard),
  // around V2's `.narrow-screen` 650px editor column (the read view keeps the
  // 720px column of the other Student pages).
  @include borna-page-container(650px);
}
// SX-01 (DESIGN-19): measured from the rendered V2 `.student-profile-editor`
// (`.back-link`, `.flow-heading`, `.flow-card`, `.form-stack`) at 1440 / 390.
.student-profile-edit__back {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 18px;
  padding: 0;
  border: 0;
  background: transparent;
  color: $borna-muted;
  font: inherit;
  font-size: 12px;
  line-height: 1.75;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    color: $borna-indigo-600;
  }
  // P43-02: Back is «→» in Persian and «←» in English; `arrow` is drawn «←».
  html.is-rtl & ::v-deep svg {
    transform: scaleX(-1);
  }
  // The global `.uc-link-button:hover` underline is not part of V2's
  // `.back-link` (rendered V2: `text-decoration-line: none` on hover). TR-037.
  &:hover {
    text-decoration: none;
  }
}
.student-profile-edit__heading {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  margin: 8px 0 22px;
}
// V2 `.flow-icon`: a 50px tile with the glyph in the text colour and no fill
// (same as the tutor setup's `.ai-tutor-setup__icon`).
.student-profile-edit__heading-icon {
  display: grid;
  place-items: center;
  flex: 0 0 50px;
  width: 50px;
  height: 50px;
  border-radius: 16px;
  color: $borna-text;
}
.student-profile-edit__heading-text {
  min-width: 0;
}
.student-profile-edit__eyebrow {
  margin: 0;
  font-size: 12px;
  font-weight: $borna-fw-extrabold;
  line-height: 1.75;
  color: $borna-indigo-600;
}
.student-profile-edit__title {
  margin: 6px 0 7px;
  font-size: 25px;
  font-weight: $borna-fw-regular;
  line-height: 1.75;
  letter-spacing: -0.03em;
  color: $borna-ink;
}
.student-profile-edit__lead {
  margin: 0;
  font-size: 12px;
  line-height: 1.8;
  color: $borna-muted;
}
// V2 `.flow-card`
.student-profile-edit__card {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 22px;
  border: 1px solid $borna-border;
  border-radius: 24px;
  background: $borna-surface;
  box-shadow: $borna-shadow-card;
}
.student-profile-edit__link {
  margin-top: 8px;
  padding: 0;
  border: 0;
  background: transparent;
  color: $borna-indigo-600;
  font: inherit;
  font-size: 12px;
  font-weight: $borna-fw-bold;
  cursor: pointer;
}
.uc-form-error {
  @include font-category('helper-text');
  color: $student-error;
  margin: 6px 0 0;
}
.uc-form-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  margin-inline-end: 8px;
  border: 2px solid rgba(255, 255, 255, 0.5);
  border-top-color: #fff;
  border-radius: 50%;
  animation: student-profile-edit-spin 0.7s linear infinite;
  vertical-align: middle;
}
@keyframes student-profile-edit-spin {
  to {
    transform: rotate(360deg);
  }
}
.success {
  margin: 0;
  padding: 10px 14px;
  border-radius: $borna-radius-md;
  background: $borna-teal-50;
  color: $borna-teal-700;
  font-size: 12px;
  line-height: 1.8;
}
// The card is a 16px flex stack (V2 `.form-stack`); rows and fields carry no
// margins of their own.
.student-profile-edit__row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.student-profile-edit__field {
  margin: 0;
  max-width: none;
}
// P42-08: a field plus its inline error; `min-width: 0` keeps the two-column
// grid from stretching around a long message.
.student-profile-edit__cell {
  min-width: 0;
}
// An invalid select takes the same red outline as an invalid text input.
.student-profile-edit__cell ::v-deep .DropdownList-select[aria-invalid='true'] {
  border-color: $student-error;
}
.student-profile-edit__card ::v-deep .uc-form-element,
.student-profile-edit__card ::v-deep .DropdownList {
  margin: 0;
}
// DropdownList.vue itself is untouched (frozen shared component); these
// ::v-deep rules only reach its rendered internals WHEN mounted inside this
// component's own template, via the normal Vue scoped-CSS descendant
// mechanism -- they never apply to any other DropdownList usage in the app.
::v-deep .DropdownList-select {
  box-sizing: border-box;
  height: 50px;
  padding: 0 14px;
  border: 1px solid $borna-border;
  border-radius: 14px;
  background: $borna-surface;
  color: $borna-text;
  font-size: 16px;
}
::v-deep .DropdownList-select:focus {
  border-color: $borna-indigo-600;
  box-shadow: 0 0 0 4px $borna-indigo-50;
  outline: none;
}
::v-deep .DropdownList-label,
.uc-form-label {
  display: block;
  margin: 0 0 8px;
  font-size: 13px;
  font-weight: $borna-fw-bold;
  line-height: 1.75;
  color: $borna-text;
}
.uc-form-text-input {
  box-sizing: border-box;
  height: 50px;
  padding: 0 14px;
  border: 1px solid $borna-border;
  border-radius: 14px;
  background: $borna-surface;
  color: $borna-text;
  font-size: 16px;
}
.uc-form-text-input:focus {
  border-color: $borna-indigo-600;
  box-shadow: 0 0 0 4px $borna-indigo-50;
  outline: none;
}
.uc-form-text-input[aria-invalid='true'] {
  border-color: $student-error;
}
// V2 `.button--primary.button--full`
.student-profile-edit__save {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 48px;
  margin-top: 4px;
  padding: 0 19px;
  border: 1px solid transparent;
  border-radius: 14px;
  background-color: $borna-indigo-600;
  box-shadow: 0 8px 20px rgba(49, 75, 214, 0.22);
  color: #fff;
  font-size: 14px;
  font-weight: $borna-fw-extrabold;
  line-height: 1.75;
}
.student-profile-edit__save:hover:not(:disabled),
.student-profile-edit__save:active:not(:disabled) {
  background-color: $borna-indigo-900;
}
.student-profile-edit__save:disabled {
  opacity: 0.42;
  box-shadow: none;
  cursor: not-allowed;
}
// V2 `.profile-location-fields`
.student-profile-edit__fieldset {
  display: block;
  min-inline-size: 0;
  margin: 0;
  padding: 14px;
  border: 1px solid $borna-border;
  border-radius: 16px;
}
.student-profile-edit__fieldset legend {
  // TR-038: the global reboot `legend { width: 100% }` made the title cut the
  // whole top border (574 of 604px). V2's legend is shrink-to-fit (rendered V2:
  // 61px), so only the title's own width interrupts the border.
  width: auto;
  margin: 0 0 9px;
  padding: 0;
  font-size: 12px;
  font-weight: $borna-fw-bold;
  line-height: 1.75;
  color: $borna-text;
}
// V2 `.notice--soft`
.notice {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 8px 0 0;
  padding: 13px;
  border-radius: 14px;
  background: $borna-teal-50;
  color: $borna-teal-700;
  font-size: 11px;
  line-height: 1.8;
}
.notice svg {
  flex: 0 0 auto;
  margin-top: 1px;
}
.ltr {
  direction: ltr;
  unicode-bidi: isolate;
}
// V2 `@media (width<=620px)`
@include borna-phone {
  .student-profile-edit__row {
    grid-template-columns: 1fr;
  }
  .student-profile-edit__title {
    font-size: 21px;
  }
  .student-profile-edit__card {
    padding: 17px;
    border-radius: 20px;
  }
}
</style>
